import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";

import type { Dance } from "@recital-master/shared";

import { DownloadIcon } from "@/design/icons";
import { Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { createDownloadGrant } from "@/firebase/callables";

export type DanceDownloadButtonProps = {
  dance: Dance;
};

export function DanceDownloadButton({ dance }: DanceDownloadButtonProps) {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");

  const download = async () => {
    if (state === "loading") return;
    setState("loading");
    try {
      const grant = await createDownloadGrant({
        performanceId: dance.performanceId,
        danceId: dance.danceId
      });
      const anchor = document.createElement("a");
      anchor.href = grant.url;
      anchor.download = "";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setState("idle");
    } catch {
      setState("error");
    }
  };

  const error = state === "error";
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={error ? "Retry dance download" : "Download dance"}
      accessibilityState={{
        busy: state === "loading",
        disabled: state === "loading"
      }}
      disabled={state === "loading"}
      onPress={download}
      style={({ pressed, hovered, focused }) => [
        styles.button,
        (state === "loading" || hovered || focused) && styles.surface,
        pressed && styles.pressed
      ]}
    >
      {({ hovered, focused }) => (
        <>
          <View style={state === "loading" && styles.contentHidden}>
            {error ? (
              <Text variant="smallStrong" color={palette.danger}>Retry</Text>
            ) : (
              <DownloadIcon
                size={16}
                color={hovered || focused ? palette.text : palette.muted}
              />
            )}
          </View>
          {state === "loading" ? (
            <View style={styles.spinnerOverlay}>
              <ActivityIndicator size="small" color={palette.textSoft} />
            </View>
          ) : null}
        </>
      )}
    </Pressable>
  );
}

// Trailing-cluster manners: a bare glyph like the watched check beside it —
// no ring at rest. The circle exists only under the pointer, as a wash.
const styles = StyleSheet.create({
  button: {
    alignItems: "center",
    borderRadius: radius.pill,
    height: 36,
    justifyContent: "center",
    minWidth: 36,
    paddingHorizontal: space.sm
  },
  surface: {
    backgroundColor: palette.coolWashStrong
  },
  contentHidden: {
    opacity: 0
  },
  pressed: {
    opacity: 0.75
  },
  spinnerOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center"
  }
});
